import create from "zustand";
import * as classwizeConfigApi from "js/api/classwizeConfigApi";
import { ClasswizeConfig } from "js/api/classwizeConfigApi";

// Interface for support contact details
export interface SupportContactDetails {
	enable_support_chatbot?: boolean;
	support_chatbot_id?: string;
	support_contact_email?: string;
	support_contact_name?: string;
	support_contact_phone?: string;
	support_message?: string;
}

// Create the Zustand store with proper typing
export const useClasswizeConfigStore = create<IClasswizeConfigStore>((set) => ({
	data: {} as ClasswizeConfig,
	isFetching: false,
	lastFetched: null,

	fetch: async () => {
		set({ isFetching: true });
		try {
			const data = await classwizeConfigApi.getClasswizeConfig();
			set({
				data: data as ClasswizeConfig,
				isFetching: false,
				lastFetched: Date.now(),
			});
		} catch (error) {
			set({ isFetching: false });
			throw error;
		}
	},
}));

// Legacy compatibility functions - these maintain the exact same API
export const getClasswizeTenantKey = (): string => {
	const store = useClasswizeConfigStore.getState();
	return store.data.tenant_key || "";
};

export const getFeatures = (): string[] => {
	const store = useClasswizeConfigStore.getState();
	return store.data.classroom_features_enabled || [];
};

export const getClasswizeConfig = (): ClasswizeConfig => {
	const store = useClasswizeConfigStore.getState();
	return store.data;
};

export const getSupportContactDetails = (): SupportContactDetails => {
	const store = useClasswizeConfigStore.getState();
	return {
		enable_support_chatbot: store.data.enable_support_chatbot,
		support_chatbot_id: store.data.support_chatbot_id,
		support_contact_email: store.data.support_contact_email,
		support_contact_name: store.data.support_contact_name,
		support_contact_phone: store.data.support_contact_phone,
		support_message: store.data.support_message,
	};
};

export const isMembershipOwnershipDisabled = (): boolean => {
	const store = useClasswizeConfigStore.getState();
	const classwizeConfig = store.data;

	if (!classwizeConfig.classroom_restrict_global_teachers || !classwizeConfig.classroom_allow_owner_access) {
		return false;
	}

	return !classwizeConfig.classroom_restrict_global_teachers && !classwizeConfig.classroom_allow_owner_access;
};

export const isMembershipOnly = (): boolean => {
	const store = useClasswizeConfigStore.getState();
	const classwizeConfig = store.data;

	if (!classwizeConfig.classroom_restrict_global_teachers || !classwizeConfig.classroom_allow_owner_access) {
		return false;
	}

	return classwizeConfig.classroom_restrict_global_teachers && !classwizeConfig.classroom_allow_owner_access;
};

export const isSupportContactDetailsLoaded = (): boolean => {
	const store = useClasswizeConfigStore.getState();
	const classwizeConfig = store.data;

	for (const [key, value] of Object.entries(classwizeConfig)) {
		if (key === "support_message") {
			continue;
		}
		if (value) {
			return true;
		}
	}

	return false;
};

// Interface for legacy store function
export interface IClasswizeConfigStore {
	data: ClasswizeConfig;
	isFetching: boolean;
	lastFetched: number | null;
	fetch: () => Promise<void>;
}

// createClasswizeConfigStore function for backward compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createClasswizeConfigStore = (_set: any, _get: any, _api: any, setIn: any): IClasswizeConfigStore => ({
	data: {} as ClasswizeConfig,
	isFetching: false,
	lastFetched: null,

	fetch: async (): Promise<void> => {
		setIn((state: IClasswizeConfigStore) => ({ isFetching: !state.isFetching }));
		const data = await classwizeConfigApi.getClasswizeConfig();
		setIn({ data: data as ClasswizeConfig, isFetching: false, lastFetched: Date.now() });
	},
});

export const isPauseInternetEnabled = (): boolean => {
	const classwizeConfig = useClasswizeConfigStore.getState().data;
	if (!classwizeConfig) {
		return false;
	}
	return Array.isArray(classwizeConfig.classroom_features_enabled) && classwizeConfig.classroom_features_enabled.includes("lock");
};
